import { useEffect, useState } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ZERO_DECIMAL_CURRENCIES = new Set([
  'BIF', 'CLP', 'DJF', 'GNF', 'JPY', 'KMF', 'KRW', 'MGA', 'PYG',
  'RWF', 'VND', 'VUV', 'XAF', 'XOF', 'XPF', 'UGX',
]);
const REGION_CURRENCY = {
  GB: 'GBP', US: 'USD', CA: 'CAD', AU: 'AUD', NZ: 'NZD', NG: 'NGN', ZA: 'ZAR', IN: 'INR',
  AE: 'AED', KE: 'KES', GH: 'GHS', CN: 'CNY', JP: 'JPY', CH: 'CHF', SG: 'SGD',
  EG: 'EGP', PK: 'PKR', BD: 'BDT', PH: 'PHP', MY: 'MYR', ID: 'IDR', BR: 'BRL', MX: 'MXN',
  SA: 'SAR', QA: 'QAR', TZ: 'TZS', UG: 'UGX', RW: 'RWF', ZM: 'ZMW', SE: 'SEK', NO: 'NOK',
  DK: 'DKK', PL: 'PLN', TR: 'TRY', HK: 'HKD', KR: 'KRW', TH: 'THB', VN: 'VND', IE: 'EUR',
  DE: 'EUR', FR: 'EUR', ES: 'EUR', IT: 'EUR', NL: 'EUR', PT: 'EUR', BE: 'EUR', AT: 'EUR',
  FI: 'EUR', GR: 'EUR',
};

const TZ_REGION = {
  'Africa/Lagos': 'NG', 'Africa/Accra': 'GH', 'Africa/Nairobi': 'KE', 'Africa/Johannesburg': 'ZA',
  'Asia/Kolkata': 'IN', 'Asia/Calcutta': 'IN', 'Asia/Dubai': 'AE', 'Europe/London': 'GB',
  'Africa/Cairo': 'EG', 'Asia/Karachi': 'PK', 'Asia/Manila': 'PH',
  'Africa/Dar_es_Salaam': 'TZ', 'Africa/Kampala': 'UG', 'Africa/Kigali': 'RW', 'Africa/Lusaka': 'ZM',
  'Asia/Dhaka': 'BD', 'Asia/Singapore': 'SG', 'Asia/Tokyo': 'JP', 'Asia/Shanghai': 'CN',
  'Asia/Riyadh': 'SA', 'Asia/Qatar': 'QA', 'Asia/Kuala_Lumpur': 'MY', 'Asia/Jakarta': 'ID',
  'Asia/Hong_Kong': 'HK', 'Asia/Seoul': 'KR', 'Asia/Bangkok': 'TH', 'Asia/Ho_Chi_Minh': 'VN',
  'Europe/Dublin': 'IE', 'Europe/Paris': 'FR', 'Europe/Berlin': 'DE', 'Europe/Madrid': 'ES',
  'Europe/Rome': 'IT', 'Europe/Amsterdam': 'NL', 'Europe/Zurich': 'CH', 'Europe/Stockholm': 'SE',
  'Europe/Oslo': 'NO', 'Europe/Copenhagen': 'DK', 'Europe/Warsaw': 'PL', 'Europe/Istanbul': 'TR',
  'America/New_York': 'US', 'America/Chicago': 'US', 'America/Denver': 'US', 'America/Los_Angeles': 'US',
  'America/Toronto': 'CA', 'America/Vancouver': 'CA', 'America/Sao_Paulo': 'BR', 'America/Mexico_City': 'MX',
  'Australia/Sydney': 'AU', 'Australia/Melbourne': 'AU', 'Australia/Perth': 'AU', 'Pacific/Auckland': 'NZ',
};

export const detectCountry = () => {
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (TZ_REGION[timezone]) return TZ_REGION[timezone];
    const locales = navigator.languages?.length ? navigator.languages : [navigator.language];
    for (const locale of locales) {
      const region = new Intl.Locale(locale).maximize().region;
      if (region && REGION_CURRENCY[region]) return region;
    }
  } catch (e) {
    return 'GB';
  }
  return 'GB';
};

export const currencyForCountry = (country) => REGION_CURRENCY[(country || '').toUpperCase()] || 'GBP';

export const useLocalPrice = (countryOverride) => {
  const detectedCountry = detectCountry();
  const savedCountry = (countryOverride || '').toUpperCase();
  const country = !savedCountry || (savedCountry === 'GB' && detectedCountry !== 'GB')
    ? detectedCountry
    : savedCountry === 'UK' ? 'GB' : savedCountry;
  const expectedCurrency = currencyForCountry(country);
  const [context, setContext] = useState(() => (
    expectedCurrency === 'GBP' ? { country, currency: 'GBP', rate: 1 } : null
  ));
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    if (expectedCurrency === 'GBP') {
      setContext({ country, currency: 'GBP', rate: 1 });
      setError(null);
      return () => { active = false; };
    }

    setContext(null);
    setError(null);
    fetch(`${API_URL}/api/payments/pricing-context?country=${encodeURIComponent(country)}`)
      .then(async (response) => {
        const data = await response.json();
        if (!response.ok) throw new Error(data.detail || 'Local currency pricing is unavailable');
        return data;
      })
      .then((data) => { if (active) setContext(data); })
      .catch((err) => { if (active) setError(err.message); });

    return () => { active = false; };
  }, [country, expectedCurrency]);

  const currency = context?.currency || expectedCurrency;
  const activeContext = context?.country === country ? context : null;
  const format = (gbp, { decimals = 2 } = {}) => {
    if (!activeContext) return error ? 'Price unavailable' : 'Loading price...';
    const amount = Number(gbp || 0) * activeContext.rate;
    const fractionDigits = ZERO_DECIMAL_CURRENCIES.has(currency) ? 0 : decimals;
    try {
      return new Intl.NumberFormat(`en-${activeContext.country}`, {
        style: 'currency',
        currency,
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
      }).format(amount);
    } catch (e) {
      return `${currency} ${amount.toFixed(fractionDigits)}`;
    }
  };

  return {
    country,
    currency: activeContext?.currency || expectedCurrency,
    isConverted: currency !== 'GBP' && !!activeContext,
    isLoading: !activeContext && !error,
    error,
    format,
  };
};
